import { PUBLIC_SECTIONS } from '@components/TripOverview/core/publicSectionRoutes'
import type { TripOverviewSection } from '@components/TripOverview/core/types'
import type { AdminFunnelPlatform, AdminFunnelStep, AdminSortOrder } from '@orbit/api-types'
import { QueryKey } from '@tanstack/react-query'

/**
 * Single source of truth for every react-query cache key. Cache identity is the
 * EXACT tuple — do not reorder, retype, or coerce elements, or invalidation
 * silently drifts. Builders accept `string | undefined` because the hooks guard
 * an absent trip id with `enabled`.
 */
export const queryKeys = {
	// Trips
	trips: (): QueryKey => ['trips'],
	trip: (tripId: string | undefined): QueryKey => ['trip', tripId],
	tripMembers: (tripId: string | undefined): QueryKey => ['tripMembers', tripId],
	/**
	 * The trip home's aggregated bento. Deliberately outside the per-entity keys
	 * and the realtime invalidation map — `useTripOverview` revalidates it on
	 * every mount instead (see that hook).
	 */
	tripOverview: (tripId: string | undefined): QueryKey => ['tripOverview', tripId],
	tripDayInfos: (tripId: string | undefined): QueryKey => ['tripDayInfos', tripId],
	shareSettings: (tripId: string | undefined): QueryKey => ['shareSettings', tripId],
	/** A trip's pending Join requests, as its owner reviews them. */
	joinRequests: (tripId: string | undefined): QueryKey => ['joinRequests', tripId],
	/** My own Join requests, pending and denied, across every trip. */
	myJoinRequests: (): QueryKey => ['myJoinRequests'],

	// Trip resources
	events: (tripId: string | undefined): QueryKey => ['events', tripId],
	expenses: (tripId: string | undefined): QueryKey => ['expenses', tripId],
	paymentsOwed: (tripId: string | undefined): QueryKey => ['paymentsOwed', tripId],
	payups: (tripId: string | undefined): QueryKey => ['payups', tripId],
	payup: (tripId: string | undefined, payUpId: number | undefined): QueryKey => [
		'payup',
		tripId,
		payUpId,
	],
	flights: (tripId: string | undefined): QueryKey => ['flights', tripId],
	accommodations: (tripId: string | undefined): QueryKey => ['accommodations', tripId],
	ideas: (tripId: string | undefined): QueryKey => ['ideas', tripId],
	travelSegments: (tripId: string | undefined): QueryKey => ['travel-segments', tripId],
	files: (tripId: string | undefined): QueryKey => ['files', tripId],
	/** My unread Chat count: the Chat button's badge. The Chat's own stream is not a query. */
	chatUnread: (tripId: string | undefined): QueryKey => ['chatUnread', tripId],
	/** A draft Message's link, unfurled by the server, by the link. Nothing changes it. */
	chatLinkPreview: (tripId: string | undefined, url: string | null): QueryKey => ['chatLinkPreview', tripId, url],

	/**
	 * A destination's Suggestions, by the name they're looked up for. Google's,
	 * not the trip's: no trip's changes touch them, so nothing invalidates them.
	 */
	placeSuggestions: (placeName: string | null): QueryKey => ['placeSuggestions', placeName],

	/**
	 * Every key whose data changes with itinerary content. The realtime
	 * `itinerary_updated` handler and both import-completion paths invalidate
	 * exactly this trio.
	 */
	itineraryContentKeys: (tripId: string | undefined): QueryKey[] => [
		queryKeys.events(tripId),
		queryKeys.travelSegments(tripId),
		queryKeys.tripDayInfos(tripId),
	],

	/**
	 * Every key a change of who is in a trip shows up in: the trip, its members, its
	 * home and my trips list (each trip's member count). Approving a request, removing
	 * a member and turning Join approval off all invalidate exactly this set.
	 */
	membershipKeys: (tripId: string | undefined): QueryKey[] => [
		queryKeys.trips(),
		queryKeys.trip(tripId),
		queryKeys.tripMembers(tripId),
		queryKeys.tripOverview(tripId),
	],

	// Itinerary import
	itineraryImport: (
		tripId: string | undefined,
		importId: string | undefined,
	): QueryKey => ['itinerary-import', tripId, importId],
	itineraryImports: (tripId: string | undefined): QueryKey => ['itinerary-imports', tripId],
	/**
	 * Public per-section detail key, derived from the section's endpoint name in
	 * `PUBLIC_SECTIONS` — the same registry that drives the URL the hook fetches,
	 * so key and endpoint cannot drift apart. `publicSection('itinerary', code)`
	 * reproduces the pre-registry `['public-itinerary', code]` literal exactly.
	 */
	publicSection: (section: TripOverviewSection, tripCode: string | undefined): QueryKey => [
		PUBLIC_SECTIONS[section].endpoint,
		tripCode,
	],
	publicItinerary: (tripCode: string | undefined): QueryKey =>
		queryKeys.publicSection('itinerary', tripCode),
	publicOverview: (tripCode: string | undefined): QueryKey => ['public-overview', tripCode],
	/** The public invitation preview a Join link resolves to. */
	joinPreview: (tripCode: string | undefined): QueryKey => ['join-preview', tripCode],

	// User / auth
	me: (): QueryKey => ['me'],
	qrToken: (): QueryKey => ['qr-token'],
	qrTokenCheck: (token: string | undefined): QueryKey => ['qr-token-check', token],

	// Admin
	adminOverview: (from: string, to: string, includeInternal: boolean): QueryKey => [
		'admin',
		'overview',
		from,
		to,
		includeInternal,
	],
	adminOnboarding: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'onboarding', from, to, page, pageSize, q, includeInternal],
	adminFunnel: (
		from: string,
		to: string,
		platform: AdminFunnelPlatform,
		includeInternal: boolean,
	): QueryKey => ['admin', 'funnel', from, to, platform, includeInternal],
	adminFunnelStep: (
		step: AdminFunnelStep | null,
		from: string,
		to: string,
		platform: AdminFunnelPlatform,
		includeInternal: boolean,
		page: number,
		pageSize: number,
	): QueryKey => ['admin', 'funnel', 'step', step, from, to, platform, includeInternal, page, pageSize],
	adminUser: (id: number): QueryKey => ['admin', 'user', id],
	adminUserAppEvents: (
		id: number,
		page: number,
		pageSize: number | undefined,
		sort: AdminSortOrder,
	): QueryKey => ['admin', 'user', id, 'app-events', page, pageSize, sort],
	adminUsers: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'users', from, to, page, pageSize, q, includeInternal],
	adminTrip: (id: number): QueryKey => ['admin', 'trip', id],
	adminTrips: (
		from: string,
		to: string,
		page: number,
		pageSize: number,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'trips', from, to, page, pageSize, q, includeInternal],
	adminEvent: (id: number): QueryKey => ['admin', 'event', id],
	adminEvents: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'events', from, to, page, pageSize, q, includeInternal],
	adminExpense: (id: number): QueryKey => ['admin', 'expense', id],
	adminExpenses: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'expenses', 'list', from, to, page, pageSize, q, includeInternal],
	adminIdea: (id: number): QueryKey => ['admin', 'idea', id],
	adminIdeas: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		q: string | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'ideas', from, to, page, pageSize, q, includeInternal],
	adminPayups: (
		from: string,
		to: string,
		page: number,
		pageSize: number | undefined,
		includeInternal: boolean,
	): QueryKey => ['admin', 'payups', 'list', from, to, page, pageSize, includeInternal],
}
